import type { ShipUpgrades } from '../gameplay/upgrades';
import { UPGRADES } from '../gameplay/upgrades';

/**
 * Compact HUD plate for the FPS mining cutter. Sits at the bottom-centre
 * of the screen on planets and surfaces the current state of the tool:
 *
 *   - hidden when the tool isn't bought yet (no point teasing it),
 *   - showing the `2` hint when bought but stowed,
 *   - lit up while the cutter is equipped.
 *
 * Visibility per game-mode is gated by `setMode('show'|'hide')` from the
 * GameDirector — we only display this on planet surfaces, not in space or
 * inside the station.
 */
export class MiningToolHUD {
  readonly element: HTMLElement;

  private readonly iconEl: HTMLElement;
  private readonly labelEl: HTMLElement;
  private readonly subEl: HTMLElement;

  private readonly unsubscribe: () => void;
  private equipped = false;
  private modeAllowed = false;
  private unlocked = false;

  constructor(shipUpgrades: ShipUpgrades) {
    this.element = document.createElement('div');
    this.element.className = 'mining-tool-hud';

    this.iconEl = document.createElement('div');
    this.iconEl.className = 'mining-tool-hud-icon';
    this.iconEl.innerHTML = UPGRADES['fps-mining-tool'].icon;

    const text = document.createElement('div');
    text.className = 'mining-tool-hud-text';

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'mining-tool-hud-label';
    this.labelEl.textContent = 'Mining Cutter';

    this.subEl = document.createElement('div');
    this.subEl.className = 'mining-tool-hud-sub';
    this.subEl.innerHTML = '<kbd>2</kbd> equip';

    text.append(this.labelEl, this.subEl);
    this.element.append(this.iconEl, text);

    this.unsubscribe = shipUpgrades.subscribe((snap) => {
      this.unlocked = snap.has('fps-mining-tool');
      this.refresh();
    });
  }

  setEquipped(equipped: boolean): void {
    if (this.equipped === equipped) return;
    this.equipped = equipped;
    this.refresh();
  }

  show(): void {
    if (this.modeAllowed) return;
    this.modeAllowed = true;
    this.refresh();
  }

  hide(): void {
    if (!this.modeAllowed) return;
    this.modeAllowed = false;
    this.refresh();
  }

  dispose(): void {
    this.unsubscribe();
    this.element.remove();
  }

  private refresh(): void {
    const visible = this.modeAllowed && this.unlocked;
    this.element.classList.toggle('is-visible', visible);
    this.element.classList.toggle('is-equipped', this.equipped);
    if (this.equipped) {
      this.subEl.innerHTML = '<kbd>2</kbd> stow · <kbd>LMB</kbd> mine';
    } else {
      this.subEl.innerHTML = '<kbd>2</kbd> equip';
    }
  }
}
